// Pure tracking-health chart-model builders for the admin page, ported from react/src/Helpers/brand_health_helpers.js
// and trimmed to the builders the admin detail renders; converging both apps onto one shared package is the recorded follow-up.
import { brandHealth } from '@shopmy/universal';
const moment = require('moment');

// The shared event vocabulary lives in @shopmy/universal — re-exported here so consumers keep one import.
export const BRAND_HEALTH_EVENT_TYPES = brandHealth.BRAND_HEALTH_EVENT_TYPES;
export const BRAND_HEALTH_OUTCOME_KEY_PREFIX = brandHealth.BRAND_HEALTH_OUTCOME_KEY_PREFIX;
export const BRAND_HEALTH_OUTCOME_SEVERITY = brandHealth.BRAND_HEALTH_OUTCOME_SEVERITY;
export const BRAND_HEALTH_EVENT_HEALTH_INDICATOR = brandHealth.BRAND_HEALTH_INDICATOR_KEYS;

// One line per orderTrackedVia* bucket. The buckets partition orderConfirmationTracked (an order
// counts under exactly one source), so the lines sum to the tracked series. The older *IsPresent
// keys only say a field existed on the event and would count a click order carrying an unrelated
// coupon under both click and custom code.
export const ORDER_ATTRIBUTION_LINES = [
  { key: BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderTrackedViaClickId, label: 'Tracked via click', tone: 'attr-click' },
  { key: BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderTrackedViaIpMatch, label: 'Tracked via IP', tone: 'attr-ip' },
  { key: BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderTrackedViaDiscountCode, label: 'Tracked via custom code', tone: 'attr-code' },
  { key: BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderTrackedViaOffer, label: 'Tracked via offer code', tone: 'attr-offer' },
  { key: BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderTrackedViaUnknown, label: 'Tracked, source unknown', tone: 'attr-unknown' }
];

export const BRAND_HEALTH_SEEN_OUTCOME_PREFIXES = [
  BRAND_HEALTH_OUTCOME_KEY_PREFIX[BRAND_HEALTH_EVENT_TYPES.CLICK],
  BRAND_HEALTH_OUTCOME_KEY_PREFIX[BRAND_HEALTH_EVENT_TYPES.CLICK_CONFIRMATION],
  BRAND_HEALTH_OUTCOME_KEY_PREFIX[BRAND_HEALTH_EVENT_TYPES.ORDER_SEEN],
  BRAND_HEALTH_OUTCOME_KEY_PREFIX[BRAND_HEALTH_EVENT_TYPES.ORDER_UPDATE_SEEN],
  BRAND_HEALTH_OUTCOME_KEY_PREFIX[BRAND_HEALTH_EVENT_TYPES.ORDER_CANCEL_SEEN]
];

// clickSuccess deliberately excludes bots and replays, so the true click total is the three click
// series summed; the other seen bases count every received event already.
export const BRAND_HEALTH_TOTAL_SEEN_KEYS = [
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.clickSuccess,
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.clickBotBlocked,
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.clickDuplicate,
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.clickHandlerError,
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.clickConfirmationSuccess,
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderConfirmationSeen,
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderUpdateSeen,
  BRAND_HEALTH_EVENT_HEALTH_INDICATOR.orderCancellationSeen
];

export const BRAND_HEALTH_LAST_EVENT_COLUMN = {
  [BRAND_HEALTH_EVENT_TYPES.CLICK]: 'lastClickAt',
  [BRAND_HEALTH_EVENT_TYPES.CLICK_CONFIRMATION]: 'lastClickConfirmationAt',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_SEEN]: 'lastOrderSeenAt',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_TRACKED]: 'lastOrderTrackedAt',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_UPDATE_SEEN]: 'lastOrderUpdateSeenAt',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_UPDATE_TRACKED]: 'lastOrderUpdateTrackedAt',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_CANCEL_SEEN]: 'lastOrderCancellationAt',
  [BRAND_HEALTH_EVENT_TYPES.ORDER_CANCEL_TRACKED]: 'lastTrackedOrderCancellationAt'
};

export const sumSeries = series => series.reduce((total, point) => total + point.y, 0);

// The one reader of the `${prefix}__${outcome}` / `${prefix}__${outcome}__${route}` bucket-key
// encoding — a change to the key shape lands here, not in every consumer.
const parseOutcomeKey = (key, prefix) => {
  if (!key.startsWith(`${prefix}__`)) return null;
  const [outcome, route] = key.slice(prefix.length + 2).split('__');
  return { outcome, route: route || null };
};

// !keep the counts in sync with the BRAND_HEALTH_MAX_*_AGGREGATES caps in node/utils/constants/brand_health_constants.js
const GRANULARITY_WINDOWS = {
  hourly: { bucketField: 'hourlyAggregates', count: 24, unit: 'hours', format: 'YYYY-MM-DD HH:00:00', axisFormat: 'hA', tooltipFormat: 'MMM D, hA' },
  daily: { bucketField: 'dailyAggregates', count: 31, unit: 'days', format: 'YYYY-MM-DD', axisFormat: 'MMM D', tooltipFormat: 'MMM D, YYYY' },
  monthly: { bucketField: 'monthlyAggregates', count: 12, unit: 'months', format: 'YYYY-MM', axisFormat: 'MMM', tooltipFormat: 'MMMM YYYY' }
};

export const getBrandHealthRecordsForScope = (allBrandHealth, domain) =>
  domain ? [allBrandHealth?.[domain]].filter(Boolean) : Object.values(allBrandHealth || {});

// Outcome keys come in two shapes: `<prefix>__<outcome>` carries the authoritative count, and its
// `<prefix>__<outcome>__<route>` twin carries the per-route split — twins never join count math.
const classifyBucketBands = bucket => {
  let [success, error] = [0, 0];
  const errorDetail = [];
  const errorRouteDetail = [];
  BRAND_HEALTH_SEEN_OUTCOME_PREFIXES.forEach(prefix =>
    Object.keys(bucket).forEach(key => {
      const parsed = parseOutcomeKey(key, prefix);
      if (!parsed) return;
      const { outcome, route } = parsed;
      if (route) {
        BRAND_HEALTH_OUTCOME_SEVERITY[outcome] === 'error' && bucket[key] && errorRouteDetail.push({ prefix, outcome, route, count: bucket[key] });
        return;
      }
      const severity = BRAND_HEALTH_OUTCOME_SEVERITY[outcome];
      if (severity === 'success') success += bucket[key] || 0;
      else if (severity === 'error') {
        error += bucket[key] || 0;
        bucket[key] && errorDetail.push({ prefix, outcome, count: bucket[key] });
      }
    })
  );
  const total = BRAND_HEALTH_TOTAL_SEEN_KEYS.reduce((sum, key) => sum + (bucket[key] || 0), 0);
  const neutral = Math.max(0, total - success - error);
  return { total, success, neutral, error, errorDetail, errorRouteDetail };
};

// The combined tracker: every seen-side event across the selected domains, banded by severity,
// with the per-bucket error outcomes carried for the tooltip. Pre-enrichment buckets carry no
// outcome keys, so their volume lands in neutral — the ENRICHMENT_DATA_SINCE note owns that seam.
export const buildTrackerSeries = (allBrandHealth, options = {}) => {
  const { granularity = 'daily', domain = null } = options;
  const granularityWindow = GRANULARITY_WINDOWS[granularity] || GRANULARITY_WINDOWS.daily;
  const records = getBrandHealthRecordsForScope(allBrandHealth, domain);
  const bucketsByKey = {};
  records.forEach(record =>
    (record?.healthData?.[granularityWindow.bucketField] || []).forEach(bucket => {
      bucketsByKey[bucket.key] = bucketsByKey[bucket.key] || [];
      bucketsByKey[bucket.key].push(bucket);
    })
  );

  const series = [];
  for (let i = granularityWindow.count - 1; i >= 0; i--) {
    const key = moment
      .utc()
      .subtract(i, granularityWindow.unit)
      .format(granularityWindow.format);
    const bands = { x: key, total: 0, success: 0, neutral: 0, error: 0 };
    const detailByOutcomeKey = {};
    const routeDetailByOutcomeKey = {};
    (bucketsByKey[key] || []).forEach(bucket => {
      const bucketBands = classifyBucketBands(bucket);
      bands.total += bucketBands.total;
      bands.success += bucketBands.success;
      bands.neutral += bucketBands.neutral;
      bands.error += bucketBands.error;
      bucketBands.errorDetail.forEach(row => {
        const outcomeKey = `${row.prefix}__${row.outcome}`;
        detailByOutcomeKey[outcomeKey] = detailByOutcomeKey[outcomeKey] ? { ...row, count: detailByOutcomeKey[outcomeKey].count + row.count } : row;
      });
      bucketBands.errorRouteDetail.forEach(row => {
        const routeKey = `${row.prefix}__${row.outcome}__${row.route}`;
        routeDetailByOutcomeKey[routeKey] = routeDetailByOutcomeKey[routeKey]
          ? { ...row, count: routeDetailByOutcomeKey[routeKey].count + row.count }
          : row;
      });
    });
    bands.errorDetail = Object.values(detailByOutcomeKey).sort((a, b) => b.count - a.count);
    bands.errorRouteDetail = Object.values(routeDetailByOutcomeKey).sort((a, b) => b.count - a.count);
    series.push(bands);
  }
  return series;
};

export const getTrackerHeadline = (allBrandHealth, options = {}) => {
  const { domain = null } = options;
  const records = getBrandHealthRecordsForScope(allBrandHealth, domain);
  let lastEventAt = null;
  const considerTimestamp = value => {
    if (value && (!lastEventAt || moment(value).isAfter(lastEventAt))) lastEventAt = value;
  };
  records.forEach(record => {
    Object.values(BRAND_HEALTH_LAST_EVENT_COLUMN).forEach(column => considerTimestamp(record?.[column]));
    // liveData lands at request time with a short TTL; the last*At columns land only when the
    // hourly consumer runs — without liveData the live dot reads stale for most of every hour.
    Object.values(record?.liveData || {}).forEach(liveEvent => considerTimestamp(liveEvent?.timestamp));
  });
  const dailySeries = buildTrackerSeries(allBrandHealth, { granularity: 'daily', domain });
  const todayBands = dailySeries[dailySeries.length - 1] || { total: 0, error: 0 };
  const windowErrorTotal = dailySeries.reduce((sum, point) => sum + point.error, 0);
  return { lastEventAt, eventsToday: todayBands.total, windowErrorTotal };
};

// Hourly keys are UTC clock hours and render in the viewer's local time; daily and monthly keys are
// calendar labels and render as written (a UTC-to-local shift would relabel the day for US viewers).
export const formatBrandHealthBucketKey = (bucketKey, granularity, display = 'axisFormat') => {
  const granularityWindow = GRANULARITY_WINDOWS[granularity] || GRANULARITY_WINDOWS.daily;
  const parsed = granularity === 'hourly' ? moment.utc(bucketKey, granularityWindow.format).local() : moment(bucketKey, granularityWindow.format);
  return parsed.format(granularityWindow[display]);
};

// The status → modifier-class map the admin status dots and verdict chips share; anything outside the
// ladder (a stale null) reads as unknown.
const SEVERITY_CLASSNAME = { BROKEN: 'is-broken', WARNING: 'is-warning', CHECK: 'is-check', OK: 'is-ok' };
export const severityClassName = status => SEVERITY_CLASSNAME[status] || 'is-unknown';

// Sums one bucketKey across the scoped records over the granularity's rolling window (hourly = last
// 24h, daily = 31d, monthly = 12mo), zero-filling gaps. Window keys are UTC to match the SQS consumer.
export const buildBucketSeriesForRecords = (records, granularity, bucketKey) => {
  const granularityWindow = GRANULARITY_WINDOWS[granularity] || GRANULARITY_WINDOWS.daily;
  const countsByBucketKey = {};
  records.forEach(record =>
    (record?.healthData?.[granularityWindow.bucketField] || []).forEach(bucket => {
      countsByBucketKey[bucket.key] = (countsByBucketKey[bucket.key] || 0) + (bucket[bucketKey] || 0);
    })
  );
  const series = [];
  for (let i = granularityWindow.count - 1; i >= 0; i--) {
    const key = moment
      .utc()
      .subtract(i, granularityWindow.unit)
      .format(granularityWindow.format);
    series.push({ x: key, y: countsByBucketKey[key] || 0 });
  }
  return series;
};

// A family's error series from the base outcome keys (authoritative); the per-route twins never
// join count math.
export const buildFamilyErrorSeriesForRecords = (records, granularity, eventTypes) => {
  const granularityWindow = GRANULARITY_WINDOWS[granularity] || GRANULARITY_WINDOWS.daily;
  const prefixes = (eventTypes || []).map(eventType => BRAND_HEALTH_OUTCOME_KEY_PREFIX[eventType]);
  const countsByBucketKey = {};
  records.forEach(record =>
    (record?.healthData?.[granularityWindow.bucketField] || []).forEach(bucket =>
      prefixes.forEach(prefix =>
        Object.keys(bucket).forEach(key => {
          const parsed = parseOutcomeKey(key, prefix);
          if (!parsed) return;
          const { outcome, route: keyRoute } = parsed;
          if (keyRoute || BRAND_HEALTH_OUTCOME_SEVERITY[outcome] !== 'error') return;
          countsByBucketKey[bucket.key] = (countsByBucketKey[bucket.key] || 0) + (bucket[key] || 0);
        })
      )
    )
  );

  const errorSeries = [];
  for (let i = granularityWindow.count - 1; i >= 0; i--) {
    const key = moment
      .utc()
      .subtract(i, granularityWindow.unit)
      .format(granularityWindow.format);
    errorSeries.push({ x: key, y: countsByBucketKey[key] || 0 });
  }
  return errorSeries;
};

// The family drill-down cards share the tracker's chart engine: the family's main series rides the
// primary line, its paired series (tracked, or confirmations on the merged clicks card) sits under
// it in the severity tone. Line labels come from the family config so merged cards read naturally.
export const buildFamilyChartModel = (records, family, options = {}) => {
  const { granularity = 'daily' } = options;
  const seenLabel = family.seenLabel || 'Received';
  const trackedLabel = family.trackedLabel || 'Tracked';
  const receivedSeries = buildBucketSeriesForRecords(records, granularity, family.seenKey);
  const trackedSeries = family.trackedKey ? buildBucketSeriesForRecords(records, granularity, family.trackedKey) : null;
  const attributionSeries = (family.attributionLines || []).map(attribution => ({
    ...attribution,
    series: buildBucketSeriesForRecords(records, granularity, attribution.key)
  }));
  const errorSeries = buildFamilyErrorSeriesForRecords(records, granularity, family.outcomeEventTypes);

  const lines = [
    {
      key: 'received',
      tone: 'gmv',
      axis: 'left',
      unitFamily: 'count',
      isPrimary: true,
      hasArea: true,
      values: receivedSeries.map(point => point.y)
    }
  ];
  trackedSeries &&
    lines.push({
      key: 'tracked',
      tone: 'gmv',
      axis: 'left',
      unitFamily: 'count',
      isPrimary: false,
      isDashed: true,
      values: trackedSeries.map(point => point.y)
    });
  attributionSeries.forEach(attribution =>
    lines.push({
      key: attribution.key,
      tone: attribution.tone,
      axis: 'left',
      unitFamily: 'count',
      isPrimary: false,
      values: attribution.series.map(point => point.y)
    })
  );
  lines.push({ key: 'error', tone: 'error', axis: 'left', unitFamily: 'count', isPrimary: false, values: errorSeries.map(point => point.y) });

  const tooltipRowsByIndex = receivedSeries.map((point, idx) => {
    const rows = [{ key: 'received', tone: 'gmv', label: seenLabel, value: point.y.toLocaleString() }];
    trackedSeries && rows.push({ key: 'tracked', tone: 'gmv', isDashed: true, label: trackedLabel, value: trackedSeries[idx].y.toLocaleString() });
    attributionSeries.forEach(attribution =>
      rows.push({ key: attribution.key, tone: attribution.tone, label: attribution.label, value: attribution.series[idx].y.toLocaleString() })
    );
    rows.push({ key: 'error', tone: 'error', label: 'Errors', value: errorSeries[idx].y.toLocaleString() });
    return rows;
  });

  return {
    dailySeries: receivedSeries.map(point => ({ date: point.x })),
    lens: 'tracking',
    todayIdx: receivedSeries.length - 1,
    frozen: true,
    lines,
    tooltipRowsByIndex,
    formatXLabel: bucketKey => formatBrandHealthBucketKey(bucketKey, granularity),
    formatTooltipDate: bucketKey => formatBrandHealthBucketKey(bucketKey, granularity, 'tooltipFormat'),
    ariaLabel: `${family.name}: ${seenLabel.toLowerCase()}${family.trackedKey ? ` and ${trackedLabel.toLowerCase()}` : ''} by ${
      granularity === 'monthly' ? 'month' : 'day'
    }`
  };
};

// Admin-only: every family's received (filled) + tracked (dashed) series plus the order-attribution
// breakdown, all overlaid — the per-family cards on one axis so the whole domain reads at a glance.
const COMBINED_FAMILY_TONES = ['gmv', 'emv', 'count', 'deployed'];

export const buildAllFamiliesChartModel = (records, families, options = {}) => {
  const { granularity = 'daily' } = options;
  const lineSpecs = [];
  families.forEach((family, idx) => {
    const tone = COMBINED_FAMILY_TONES[idx % COMBINED_FAMILY_TONES.length];
    const seenLabel = family.combinedSeenLabel || family.name;
    lineSpecs.push({ tone, isPrimary: true, hasArea: true, isDashed: false, seriesKey: family.seenKey, label: seenLabel });
    family.trackedKey &&
      lineSpecs.push({
        tone,
        isPrimary: false,
        hasArea: false,
        isDashed: true,
        seriesKey: family.trackedKey,
        label: family.combinedTrackedLabel || `${seenLabel} tracked`
      });
    (family.attributionLines || []).forEach(attribution =>
      lineSpecs.push({
        tone: attribution.tone,
        isPrimary: false,
        hasArea: false,
        isDashed: false,
        seriesKey: attribution.key,
        label: attribution.label
      })
    );
  });

  const seriesForSpec = lineSpecs.map(spec => buildBucketSeriesForRecords(records, granularity, spec.seriesKey));
  const xSeries = seriesForSpec[0] || [];
  const lines = lineSpecs.map((spec, idx) => ({
    key: spec.seriesKey,
    tone: spec.tone,
    axis: 'left',
    unitFamily: 'count',
    isPrimary: spec.isPrimary,
    hasArea: spec.hasArea,
    isDashed: spec.isDashed,
    values: seriesForSpec[idx].map(point => point.y)
  }));
  const tooltipRowsByIndex = xSeries.map((_, pointIdx) =>
    lineSpecs.map((spec, idx) => ({
      key: spec.seriesKey,
      tone: spec.tone,
      isDashed: spec.isDashed,
      label: spec.label,
      value: seriesForSpec[idx][pointIdx].y.toLocaleString()
    }))
  );

  return {
    dailySeries: xSeries.map(point => ({ date: point.x })),
    lens: 'tracking',
    todayIdx: xSeries.length - 1,
    frozen: true,
    lines,
    tooltipRowsByIndex,
    formatXLabel: bucketKey => formatBrandHealthBucketKey(bucketKey, granularity),
    formatTooltipDate: bucketKey => formatBrandHealthBucketKey(bucketKey, granularity, 'tooltipFormat'),
    ariaLabel: `All event families combined by ${granularity === 'monthly' ? 'month' : 'day'}`
  };
};
